import React, { useCallback, useEffect, useState } from "react";

import { TFunction, formatNumericCurrency, isEmptyObject, isPracticeDependencyCompleted, isPracticeEnabledByBundle, useGetFarms, useTranslation } from "utils";

import { CurrencyCode } from "consts";

import {
  IMPACT_PANEL_CALCULATION_TYPE,
  IMPACT_PANEL_RESULT_TYPE,
  PRACTICE_SLUG,
  Payment,
  PracticeBundle,
  PracticeSlug,
  SingleFieldOutcomes,
  VIEW_TYPE,
  ValidAggregation,
} from "models";

import { Button, ExtraInfo, Flex, Icon, LinkMoreInfo } from "components";

import { FieldTimelineBarChart } from "pages/app/field-timeline/components/FieldTimelineBarChart";
import { ImpactPanelErrorView } from "pages/app/field-timeline/components/ImpactPanelErrorView";

type FieldTimelineResultCardProps = {
  singleFieldOutcomes: SingleFieldOutcomes[];
  viewType: VIEW_TYPE;
  resultType: IMPACT_PANEL_RESULT_TYPE;
  calculationType: IMPACT_PANEL_CALCULATION_TYPE;
  showResults: boolean;
  selectedYear: number | string;
  yearRange: number[];
  reportingYear: number;
  practiceSlugs: PracticeSlug[];
  fieldSizes: Record<string, number>;
  hasMissingData: boolean;
  hasErrorData: boolean;
  setNavigateToMissingData?: (value: boolean) => void;
  practiceBundles?: PracticeBundle[];
  pastPayouts: Payment[];
};

export const FieldTimelineResultCard = ({
  viewType,
  resultType,
  calculationType,
  singleFieldOutcomes,
  showResults,
  selectedYear,
  yearRange,
  reportingYear,
  practiceSlugs,
  fieldSizes,
  hasMissingData,
  hasErrorData,
  setNavigateToMissingData,
  practiceBundles,
  pastPayouts = [],
}: FieldTimelineResultCardProps) => {
  const { t } = useTranslation(["fieldTimeline"]);

  const { currentFarm } = useGetFarms();

  const [hasNegativeImpact, setHasNegativeImpact] = useState<boolean>();
  const [hasZeroImpact, setHasZeroImpact] = useState<boolean>();
  const [showNegativePayoutDrawer, setShowNegativePayoutDrawer] = useState<boolean>(false);
  const [payout, setPayout] = useState<string>();

  const dividesPerHectares = useCallback((value: number | null, hectares: number | undefined) => {
    if (value === null || hectares === undefined) return 0;
    return value / hectares;
  }, []);

  const calculateTotalPayout = useCallback(
    (calculation: SingleFieldOutcomes, year: number | string) => {
      let totalPayout = 0;

      if (resultType === IMPACT_PANEL_RESULT_TYPE.YEARLY) {
        totalPayout = practiceSlugs.reduce((sum, practiceSlug) => {
          const yearlyPayout = safelyExtractYearlyPracticePayout(calculation, practiceSlug, Number(year), practiceBundles);
          return sum + yearlyPayout;
        }, 0);
      }

      if (resultType === IMPACT_PANEL_RESULT_TYPE.ALL_YEARS) {
        totalPayout = practiceSlugs.reduce(
          (practiceAllYearsPayout, practiceSlug) =>
            practiceAllYearsPayout +
            yearRange.reduce((yearlyTotal, year) => {
              const yearlyPayout = safelyExtractYearlyPracticePayout(calculation, practiceSlug, year, practiceBundles);
              return yearlyTotal + yearlyPayout;
            }, 0),
          0
        );
      }

      return totalPayout;
    },
    [practiceSlugs, resultType, yearRange, practiceBundles]
  );

  const setValues = useCallback(
    (year: number | string) => {
      let totalPayout = 0;
      let enrolledFieldsTotalSize = 0;

      if (viewType === VIEW_TYPE.SINGLE_FIELD || viewType === VIEW_TYPE.SIMULATION) {
        const singleFieldOutcome = singleFieldOutcomes[0];
        totalPayout = calculateTotalPayout(singleFieldOutcome, year);
        enrolledFieldsTotalSize = fieldSizes[singleFieldOutcome?.fieldId] || 0;
      } else if (viewType === VIEW_TYPE.ALL_FIELDS) {
        const fieldOutcomesByPractice = Object.values(singleFieldOutcomes);
        totalPayout = fieldOutcomesByPractice.reduce((acc, curr) => acc + calculateTotalPayout(curr, year), 0);

        // Also add past payouts to the total, for ALL_YEARS add all, otherwise just the selected year
        totalPayout += pastPayouts.reduce(
          (acc, payout) => acc + (resultType === IMPACT_PANEL_RESULT_TYPE.ALL_YEARS || payout.year === Number(year) ? payout.amount : 0),
          0
        );

        enrolledFieldsTotalSize = fieldOutcomesByPractice.reduce((totalSize, singleFieldOutcome) => {
          const cropsOutcome = singleFieldOutcome.fieldOutcomes[PRACTICE_SLUG.CROPS];
          if (!cropsOutcome?.isParticipating) return totalSize;

          if (resultType === IMPACT_PANEL_RESULT_TYPE.ALL_YEARS) return totalSize + (fieldSizes[singleFieldOutcome?.fieldId] || 0);

          if (resultType === IMPACT_PANEL_RESULT_TYPE.YEARLY) {
            if (isEmptyObject(cropsOutcome?.years ?? {})) return totalSize;
            const hasPayoutsForSelectedYear = !!(cropsOutcome?.years[selectedYear] as ValidAggregation)?.payouts?.avg;
            return hasPayoutsForSelectedYear ? totalSize + fieldSizes[singleFieldOutcome.fieldId] : totalSize;
          }

          return totalSize;
        }, 0);
      }

      setHasNegativeImpact(totalPayout < 0);
      setHasZeroImpact(totalPayout === 0);

      setPayout(
        formatPayoutValue(
          calculationType === IMPACT_PANEL_CALCULATION_TYPE.PER_HA ? dividesPerHectares(totalPayout, enrolledFieldsTotalSize) : totalPayout,
          calculationType === IMPACT_PANEL_CALCULATION_TYPE.PER_HA,
          currentFarm?.carbonProgram?.currency
        )
      );
    },
    [singleFieldOutcomes, calculationType, viewType, fieldSizes, resultType, selectedYear, dividesPerHectares, calculateTotalPayout, pastPayouts, currentFarm]
  );

  const displayResults = showResults && (viewType !== VIEW_TYPE.ALL_FIELDS ? !hasMissingData && !hasErrorData : true);

  useEffect(() => {
    setValues(selectedYear);
  }, [selectedYear, setValues]);

  return (
    <>
      {displayResults ? (
        <div data-testid="field-timeline-result-card" className="field-timeline-result-card">
          <div className="field-timeline-result-card container">
            <div className={`field-timeline-result-card container top ${hasNegativeImpact && "negative-impact"}`}>
              <Flex data-testid="field-timeline-result-card-container" vertical={false}>
                <h1 data-testid="field-timeline-result-card-value" className={`field-timeline-result-card avg value ${hasNegativeImpact && "negative-impact"}`}>
                  {payout}
                </h1>
              </Flex>
              {hasNegativeImpact && (
                <LinkMoreInfo
                  dataTestId="field-timeline-result-card-negative-payout-link-more-info"
                  className="field-timeline-result-card info-link"
                  theme="grey-small"
                  onClick={() => setShowNegativePayoutDrawer(!showNegativePayoutDrawer)}
                >
                  {t("impactPanel.negativePayoutCmsDrawer")}
                </LinkMoreInfo>
              )}
              {!hasZeroImpact && (
                <Graph
                  t={t}
                  type={resultType}
                  calculation={singleFieldOutcomes}
                  yearRange={yearRange}
                  selectedYear={selectedYear}
                  calculationType={calculationType}
                  dividesPerHectares={dividesPerHectares}
                  reportingYear={reportingYear}
                  practiceSlugs={practiceSlugs}
                  viewType={viewType}
                  fieldSizes={fieldSizes}
                  practiceBundles={practiceBundles}
                  pastPayouts={pastPayouts}
                  currency={currentFarm?.carbonProgram?.currency}
                />
              )}
            </div>
            {hasNegativeImpact && <MitigationMessage t={t} />}
          </div>
        </div>
      ) : hasErrorData ? (
        <ImpactPanelErrorView />
      ) : (
        <EmptyDataView t={t} viewType={viewType} hasMissingData={hasMissingData} setNavigateToMissingData={setNavigateToMissingData} />
      )}
      <ExtraInfo setVisible={setShowNegativePayoutDrawer} type="negative-payout" visible={showNegativePayoutDrawer} />
    </>
  );
};

const MitigationMessage = ({ t }: { t: TFunction }) => (
  <Flex data-testid="field-timeline-result-card-negative-payout-mitigation-message" className="field-timeline-result-card negative-payout" vertical>
    <p data-testid="mitigation-message-title">{t("impactPanel.mitigationMessageTitle")}</p>
    <ul>
      <li data-testid="mitigation-message1">{t("impactPanel.mitigationMessage1")}</li>
      <li data-testid="mitigation-message2">{t("impactPanel.mitigationMessage2")}</li>
      <li data-testid="mitigation-message3">{t("impactPanel.mitigationMessage3")}</li>
    </ul>
  </Flex>
);

type GraphProps = {
  calculation: SingleFieldOutcomes[];
  type: IMPACT_PANEL_RESULT_TYPE;
  selectedYear: number | string;
  yearRange: number[];
  calculationType: IMPACT_PANEL_CALCULATION_TYPE;
  t: TFunction;
  dividesPerHectares: (value: number | null, hectares: number) => number;
  reportingYear: number;
  practiceSlugs: PracticeSlug[];
  viewType: VIEW_TYPE;
  fieldSizes: Record<string, number>;
  practiceBundles?: PracticeBundle[];
  pastPayouts: Payment[];
  currency?: CurrencyCode;
};

const Graph = ({
  calculation,
  type,
  selectedYear,
  yearRange,
  calculationType,
  t,
  dividesPerHectares,
  reportingYear,
  practiceSlugs,
  viewType,
  fieldSizes,
  practiceBundles,
  pastPayouts,
  currency,
}: GraphProps) => {
  if (type === IMPACT_PANEL_RESULT_TYPE.YEARLY) {
    // We only want to show the division between practices for current or future years
    if (Number(selectedYear) < reportingYear) {
      return <p className="paragraph-s">{t("impactPanel.noPracticesPayoutSplit")}</p>;
    }

    let labels: string[] = [];
    let payouts: number[] = [];
    let fieldSizesPerPractice: number[] = [];

    if (viewType === VIEW_TYPE.SINGLE_FIELD || viewType === VIEW_TYPE.SIMULATION) {
      ({ labels, payouts, fieldSizesPerPractice } = aggregatePayouts(
        t,
        calculation[0],
        Number(selectedYear),
        practiceSlugs,
        viewType,
        fieldSizes,
        practiceBundles
      ));
    }

    if (viewType === VIEW_TYPE.ALL_FIELDS) {
      ({ labels, payouts, fieldSizesPerPractice } = calculation.reduce(
        (acc, curr, idx) => {
          // if it's the first field get also the labels
          if (idx === 0) return aggregatePayouts(t, curr, Number(selectedYear), practiceSlugs, viewType, fieldSizes);
          // aggregate single field payouts per practice
          const { payouts: singleFieldPayouts, fieldSizesPerPractice } = aggregatePayouts(t, curr, Number(selectedYear), practiceSlugs, viewType, fieldSizes);
          // add single field payouts per practice to all fields payouts per practice
          return {
            ...acc,
            payouts: acc.payouts.map((payout: number, idx: number) => payout + singleFieldPayouts[idx]),
            fieldSizesPerPractice: acc.fieldSizesPerPractice.map((size: number, idx: number) => size + fieldSizesPerPractice[idx]),
          };
        },
        { labels: [], payouts: [], fieldSizesPerPractice: [] } as { labels: string[]; payouts: number[]; fieldSizesPerPractice: number[] }
      ));
    }

    const { filteredLabels, filteredFieldsSizes, filteredPayouts } = payouts.reduce(
      (acc, payout, idx) => {
        if (payout !== 0) {
          acc.filteredLabels.push(labels[idx]);
          acc.filteredFieldsSizes.push(fieldSizesPerPractice[idx]);
          acc.filteredPayouts.push(payout);
        }
        return acc;
      },
      { filteredLabels: [] as string[], filteredFieldsSizes: [] as number[], filteredPayouts: [] as number[] }
    );

    return (
      <div data-testid="field-timeline-result-card-graph-payout-year">
        <FieldTimelineBarChart
          labels={filteredLabels}
          datasets={[
            {
              label: "",
              data:
                calculationType === IMPACT_PANEL_CALCULATION_TYPE.TOTAL
                  ? filteredPayouts
                  : filteredPayouts.map((payout, idx) => dividesPerHectares(payout, filteredFieldsSizes[idx])),
            },
          ]}
          formatFunction={formatPayoutValue}
          currency={currency}
          dividePerHa={calculationType === IMPACT_PANEL_CALCULATION_TYPE.PER_HA}
          reportingYear={reportingYear}
          resultType={IMPACT_PANEL_RESULT_TYPE.YEARLY}
          highlightReportingYearLabel={viewType !== VIEW_TYPE.SIMULATION}
        />
      </div>
    );
  }

  if (type === IMPACT_PANEL_RESULT_TYPE.ALL_YEARS) {
    const calculatePracticePayoutPerYear = (singleFieldCalculation: SingleFieldOutcomes, year: number, practiceSlugs: PracticeSlug[]): number =>
      practiceSlugs.reduce((total: number, practiceSlug: PracticeSlug) => {
        const yearlyPayout = safelyExtractYearlyPracticePayout(singleFieldCalculation, practiceSlug, year, practiceBundles);
        return total + yearlyPayout;
      }, 0);

    const calculateFieldsPayoutPerYear = (fieldsCalculations: SingleFieldOutcomes[], year: number, practiceSlugs: PracticeSlug[]): number =>
      fieldsCalculations.reduce((total: number, singleFieldCalculation) => {
        const fieldPayout = calculatePracticePayoutPerYear(singleFieldCalculation, year, practiceSlugs);
        return total + fieldPayout;
      }, 0);

    const totalPayoutsPerYear = (calculation: SingleFieldOutcomes[], perHa: boolean): number[] =>
      yearRange.map((year) => {
        const fieldsYearlyPayout = calculateFieldsPayoutPerYear(calculation, year, practiceSlugs);
        const enrolledFieldsTotalSize = Object.values(calculation).reduce((totalSize, singleFieldOutcome) => {
          const cropsOutcome = singleFieldOutcome.fieldOutcomes[PRACTICE_SLUG.CROPS];
          const isEnrolled = cropsOutcome?.isParticipating;
          if (isEmptyObject(cropsOutcome?.years ?? {})) return totalSize;
          const hasPayoutsForSelectedYear = !!(cropsOutcome?.years[year] as ValidAggregation)?.payouts?.avg;
          return isEnrolled && hasPayoutsForSelectedYear ? totalSize + fieldSizes[singleFieldOutcome.fieldId] : totalSize;
        }, 0);
        return perHa ? dividesPerHectares(fieldsYearlyPayout, enrolledFieldsTotalSize) : fieldsYearlyPayout;
      });

    // We need to add the past payouts to the array as well
    const pastPayoutAmounts = pastPayouts.map(({ amount }) => amount);
    const pastPayoutYears = pastPayouts.map(({ year }) => year);

    const payouts = [...pastPayoutAmounts, ...totalPayoutsPerYear(calculation, calculationType === IMPACT_PANEL_CALCULATION_TYPE.PER_HA)];
    const labels = [...pastPayoutYears, ...yearRange].map(String);

    const { filteredLabels, filteredPayouts } = payouts.reduce(
      (acc, p, i) => {
        if (p !== 0) {
          acc.filteredLabels.push(labels[i]);
          acc.filteredPayouts.push(p);
        }
        return acc;
      },
      { filteredLabels: [] as string[], filteredPayouts: [] as number[] }
    );

    return (
      <div data-testid="field-timeline-result-card-graph-payout-year">
        <FieldTimelineBarChart
          labels={filteredLabels}
          datasets={[
            {
              label: "",
              data: filteredPayouts,
            },
          ]}
          formatFunction={formatPayoutValue}
          dividePerHa={calculationType === IMPACT_PANEL_CALCULATION_TYPE.PER_HA}
          currency={currency}
          reportingYear={reportingYear}
          resultType={IMPACT_PANEL_RESULT_TYPE.ALL_YEARS}
          highlightReportingYearLabel={viewType !== VIEW_TYPE.SIMULATION}
        />
      </div>
    );
  }
};

const EmptyDataView = ({
  t,
  viewType,
  hasMissingData,
  setNavigateToMissingData,
}: {
  t: TFunction;
  viewType: VIEW_TYPE;
  hasMissingData: boolean;
  setNavigateToMissingData?: (value: boolean) => void;
}) => (
  <div data-testid="field-timeline-result-card-empty" className="field-timeline-result-card">
    <div className="field-timeline-result-card container empty">
      <Flex vertical={true} align="center" justify="center" style={{ height: "100%" }}>
        <Icon dataTestId="field-timeline-result-card-empty-payout-funding-icon" className="margin-bottom-100" iconName="klim/funding" size="xl" />
        <p data-testid="field-timeline-result-card-empty-title" className="field-timeline-result-card-empty-title">
          {t("impactPanel.emptyPayoutTitle")}
        </p>
        <span data-testid="field-timeline-result-card-empty-payout-description" className="field-timeline-result-card-empty-payout-description">
          {t("impactPanel.emptyPayoutDescription")}
        </span>
        {(viewType === VIEW_TYPE.SINGLE_FIELD || viewType === VIEW_TYPE.SIMULATION) && hasMissingData ? (
          <Button className="empty-card-button" size="small" type="secondary" onClick={() => setNavigateToMissingData?.(true)} dataTestId="add-data-btn">
            {t("allFieldView:fieldCell.addData")}
          </Button>
        ) : null}
      </Flex>
    </div>
  </div>
);

const aggregatePayouts = (
  t: TFunction,
  calculation: SingleFieldOutcomes,
  selectedYear: number,
  practiceSlugs: PracticeSlug[],
  viewType: VIEW_TYPE,
  fieldSizes: Record<string, number>,
  practiceBundles?: PracticeBundle[]
) => {
  const payoutAndFieldSizePerPractice = {
    [PRACTICE_SLUG.CROPS]: { payout: 0, fieldSize: 0 },
    [PRACTICE_SLUG.TILLAGES]: { payout: 0, fieldSize: 0 },
    [PRACTICE_SLUG.SEEDINGS]: { payout: 0, fieldSize: 0 },
    [PRACTICE_SLUG.MINERAL_FERTILIZERS]: { payout: 0, fieldSize: 0 },
    [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: { payout: 0, fieldSize: 0 },
    [PRACTICE_SLUG.PLANT_PROTECTIONS]: { payout: 0, fieldSize: 0 },
    [PRACTICE_SLUG.LAND_USE_CHANGES]: { payout: 0, fieldSize: 0 },
  };

  practiceSlugs.forEach((practiceSlug) => {
    const payout = safelyExtractYearlyPracticePayout(calculation, practiceSlug, selectedYear, practiceBundles);
    const fieldHasValidYearlyOutcome = hasValidYearlyOutcome(calculation, practiceSlug, selectedYear);

    if (payoutAndFieldSizePerPractice[practiceSlug]) {
      payoutAndFieldSizePerPractice[practiceSlug].payout += payout;
      if (fieldHasValidYearlyOutcome) {
        payoutAndFieldSizePerPractice[practiceSlug].fieldSize += fieldSizes[calculation.fieldId];
      }
    }
  });

  return {
    labels: [t("impactPanel.crops"), t("impactPanel.tillageSeeding"), t("impactPanel.fertilizers")],
    payouts: [
      payoutAndFieldSizePerPractice[PRACTICE_SLUG.CROPS].payout,
      payoutAndFieldSizePerPractice[PRACTICE_SLUG.TILLAGES].payout + payoutAndFieldSizePerPractice[PRACTICE_SLUG.SEEDINGS].payout,
      payoutAndFieldSizePerPractice[PRACTICE_SLUG.MINERAL_FERTILIZERS].payout + payoutAndFieldSizePerPractice[PRACTICE_SLUG.ORGANIC_FERTILIZERS].payout,
    ],
    fieldSizesPerPractice: [
      payoutAndFieldSizePerPractice[PRACTICE_SLUG.CROPS].fieldSize,
      payoutAndFieldSizePerPractice[PRACTICE_SLUG.TILLAGES].fieldSize + payoutAndFieldSizePerPractice[PRACTICE_SLUG.SEEDINGS].fieldSize,
      payoutAndFieldSizePerPractice[PRACTICE_SLUG.MINERAL_FERTILIZERS].fieldSize + payoutAndFieldSizePerPractice[PRACTICE_SLUG.ORGANIC_FERTILIZERS].fieldSize,
    ],
  };
};

const hasValidYearlyOutcome = (calculation: SingleFieldOutcomes, practiceSlug: PracticeSlug, year: number) => {
  const yearlyData = (calculation?.fieldOutcomes?.[practiceSlug]?.years?.[year] ?? {}) as ValidAggregation;
  return yearlyData?.validation?.valid && !!yearlyData?.payouts?.avg;
};

const safelyExtractYearlyPracticePayout = (
  calculation: SingleFieldOutcomes,
  practiceSlug: PracticeSlug,
  year: number,
  practiceBundles?: PracticeBundle[]
): number => {
  const fieldOutcomes = calculation?.fieldOutcomes;
  const calculationsPerPracticeSlug = fieldOutcomes[practiceSlug];

  if (!calculationsPerPracticeSlug?.isParticipating) return 0;

  if (calculationsPerPracticeSlug.required.byDependency && !isPracticeDependencyCompleted(calculation, practiceSlug, year)) return 0;

  if (practiceSlug !== PRACTICE_SLUG.CROPS && practiceBundles && !isPracticeEnabledByBundle(practiceSlug, practiceBundles)) return 0;

  const yearsCalculations = calculationsPerPracticeSlug.years ?? {};
  const yearlyOutcomesPerPractice = yearsCalculations[year] ?? {};
  const isValidNonBaselineYear = !yearlyOutcomesPerPractice?.isBaselineYear && yearlyOutcomesPerPractice?.validation?.valid;
  const avgPayout = (yearlyOutcomesPerPractice as ValidAggregation)?.payouts?.avg || 0;

  return isValidNonBaselineYear ? avgPayout : 0;
};

const formatPayoutValue = (value?: number, dividePerHectare?: boolean, currency?: CurrencyCode): string => {
  if (!value) return formatNumericCurrency(0, { decimals: 0, currency });
  const formattedValue = formatNumericCurrency(value, { decimals: 0, currency });
  return `${formattedValue}${dividePerHectare ? "/ha" : ""}`;
};
